import React, { useEffect, useState, useRef } from 'react';
import { api } from '../contexts/ServerContext';
import toast from 'react-hot-toast';
import { useServer } from '../contexts/ServerContext';
import { useAuth } from '../contexts/AuthContext';
import { steamIdTo64, getCachedAvatar, getCachedDisplayName } from '../utils/helpers';


function ActiveBansTab() {
  const { server, servers, loading: serversLoading } = useServer();
  const { user, loading: userLoading } = useAuth();
  const [bans, setBans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(0);
  const [info, setInfo] = useState('');
  const [pendingUnban, setPendingUnban] = useState({});
  const [showUnbanModal, setShowUnbanModal] = useState(false);
  const [unbanReason, setUnbanReason] = useState('');
  const [unbanTarget, setUnbanTarget] = useState(null);
  const [avatars, setAvatars] = useState({});
  const [names, setNames] = useState({});
  const timer = useRef(null);

  const fetchBans = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/punishments/active-bans`);
      setBans(res.data.bans || []);
      setInfo(res.data.info || '');
      setLastUpdate(Date.now());
    } catch (e) {
      toast.error('Ошибка при загрузке банов');
      setBans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (serversLoading || userLoading) return;
    if (!server || !user) return;
    const timeout = setTimeout(() => {
      fetchBans();
    }, 400);
    return () => timer.current && clearTimeout(timer.current);
  }, [server, user, serversLoading, userLoading]);

  useEffect(() => {
    const steamIds = bans.map(ban => ban.steamid).filter(Boolean);
    const adminIds = bans.map(ban => ban.adminSteamId).filter(Boolean);
    const allIds = Array.from(new Set([...steamIds, ...adminIds]));
    allIds.forEach(async (steamId) => {
      if (!avatars[steamId]) {
        try {
          const id64 = steamIdTo64(steamId);
          if (!id64) return;
          const url = await getCachedAvatar(id64, async () => {
            const resp = await api.get(`/api/steam/avatar/${id64}`);
            return resp.data.avatar;
          });
          setAvatars(prev => ({ ...prev, [steamId]: url }));
        } catch {
          setAvatars(prev => ({ ...prev, [steamId]: null }));
        }
      }
      if (!names[steamId]) {
        try {
          const id64 = steamIdTo64(steamId);
          if (!id64) return;
          const displayName = await getCachedDisplayName(id64, async () => {
            const resp = await api.get(`/api/players/${steamId}?server=${server}`);
            return resp.data.displayName || steamId;
          });
          setNames(prev => ({ ...prev, [steamId]: displayName }));
        } catch {
          setNames(prev => ({ ...prev, [steamId]: steamId }));
        }
      }
    });
  }, [bans]);

  const handleManualRefresh = () => {
    if (Date.now() - lastUpdate < 5 * 60 * 1000) {
      toast('Обновлять можно раз в 5 минут');
      return;
    }
    fetchBans();
  };

  const handleUnban = (steamid) => {
    setUnbanTarget(steamid);
    setUnbanReason('');
    setShowUnbanModal(true);
  };

  const confirmUnban = async () => {
    if (!unbanTarget) return;
    setPendingUnban(prev => ({ ...prev, [unbanTarget]: true }));
    try {
      await api.post('/api/punishments/active-bans/unban', { steamid: unbanTarget, server, reason: unbanReason });
      toast.success('Команда на снятие бана отправлена');
      setBans(prev => prev.filter(ban => ban.steamid !== unbanTarget));
      setShowUnbanModal(false);
      setUnbanTarget(null);
      setUnbanReason('');
    } catch (e) {
      toast.error('Ошибка при снятии бана');
    } finally {
      setPendingUnban(prev => ({ ...prev, [unbanTarget]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gmod-white ml-4">Активные баны</h1>
          <p className="text-gmod-white opacity-60 mt-2 ml-4">Логи могут быть обновлены раз в 5 минут</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleManualRefresh}
            className="px-4 py-2 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover"
          >
            Обновить
          </button>
          {user && user.rank === 'superadmin' && (
            <button
              onClick={fetchBans}
              className="px-4 py-2 bg-theme-primary-hover text-gmod-white rounded hover:bg-theme-primary"
            >
              Force Reload
            </button>
          )}
        </div>
      </div>
      <div className="bg-gmod-dark rounded-lg border border-gmod-lighterer p-6">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary"></div>
          </div>
        ) : bans.length === 0 ? (
          <p className="text-gmod-white opacity-60 text-center py-8">Активных банов нет</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bans.map((ban, idx) => {
              const unbanDate = ban.unban_date ? new Date(ban.unban_date * 1000) : null;
              // Перманент — если нет даты или дата больше чем через месяц
              const isPermanent = !ban.unban_date || (unbanDate && (unbanDate.getTime() - Date.now() > 31 * 24 * 60 * 60 * 1000));
              const soon = unbanDate && !isPermanent && (unbanDate.getTime() - Date.now() < 3 * 24 * 60 * 60 * 1000); // 3 дня
              return (
                <div
                  key={ban.id || (ban.steamid + '-' + ban.adminSteamId + '-' + ban.unban_date) || idx}
                  className={`flex flex-col bg-gmod-lighter rounded-xl border-2 p-6 shadow-lg relative
                    ${isPermanent ? 'border-theme-primary' : soon ? 'border-green-500' : 'border-orange-400'}`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    {avatars[ban.steamid] && (
                      <img
                        src={avatars[ban.steamid]}
                        alt="avatar"
                        className="w-16 h-16 min-w-[64px] rounded-full border-2 border-theme-primary bg-gmod-dark object-cover"
                      />
                    )}
                    <div className="flex flex-col min-w-0">
                      <a
                        href={`/players/${ban.steamid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-theme-primary hover:underline font-bold text-lg truncate max-w-[180px]"
                      >
                        {names[ban.steamid] || ban.steamid}
                      </a>
                      <span className="text-xs text-gmod-white opacity-60 font-mono truncate">{ban.steamid}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-theme-primary text-white rounded px-2 py-0.5">Причина</span>
                      <span className="text-gmod-white text-sm font-semibold">{ban.reason || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-gmod-blue text-white rounded px-2 py-0.5">Админ</span>
                      {ban.adminSteamId ? (
                        <span className="flex items-center gap-2">
                          {avatars[ban.adminSteamId] && (
                            <img
                              src={avatars[ban.adminSteamId]}
                              alt="admin avatar"
                              className="w-8 h-8 min-w-[32px] rounded-full border border-theme-primary bg-gmod-dark object-cover"
                            />
                          )}
                          <a
                            href={`/players/${ban.adminSteamId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-theme-primary hover:underline font-semibold truncate max-w-[120px]"
                          >
                            {names[ban.adminSteamId] || ban.adminSteamId}
                          </a>
                          <span className="text-xs text-gmod-white opacity-60 font-mono truncate">{ban.adminSteamId}</span>
                        </span>
                      ) : (
                        <span className="text-gmod-white">{ban.admin || '-'}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-gmod-yellow text-white rounded px-2 py-0.5">Дата разбана</span>
                      <span className={`text-sm font-semibold ${isPermanent ? 'text-theme-primary' : soon ? 'text-green-500' : 'text-orange-400'}`}>{isPermanent ? 'Навсегда' : unbanDate.toLocaleString('ru-RU')}</span>
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => handleUnban(ban.steamid)}
                      className="px-4 py-2 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover disabled:opacity-50"
                      disabled={pendingUnban[ban.steamid] || !user || (user.immunity || 0) < 40}
                      title={(!user || (user.immunity || 0) < 40) ? 'Недостаточно иммунитета для снятия бана (нужно 60+)' : ''}
                    >
                      {pendingUnban[ban.steamid] ? '...' : 'Снять бан'}
                    </button>
                  </div>
                  {isPermanent && (
                    <span className="absolute top-2 right-2 bg-theme-primary text-white text-xs px-2 py-0.5 rounded shadow">Навсегда</span>
                  )}
                  {soon && !isPermanent && (
                    <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded shadow">Скоро разбан</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showUnbanModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-gmod-dark rounded-lg p-6 border border-gmod-lighterer max-w-lg w-full">
            <div className="text-lg font-bold text-theme-primary mb-4">Причина снятия бана</div>
            <input
              type="text"
              className="w-full px-4 py-2 mb-4 bg-gmod-lighter border border-gmod-lighterer rounded-md text-gmod-white placeholder-gmod-white placeholder-opacity-40 focus:outline-none focus:ring-2 focus:ring-theme-primary"
              placeholder="Причина (только для истории)"
              value={unbanReason}
              onChange={e => setUnbanReason(e.target.value)}
              autoFocus
            />
            <div className="flex space-x-4">
              <button
                className="px-4 py-2 bg-theme-primary text-gmod-white rounded hover:bg-theme-primary-hover"
                disabled={pendingUnban[unbanTarget] || !unbanReason.trim()}
                onClick={confirmUnban}
              >
                Подтвердить
              </button>
              <button
                className="px-4 py-2 bg-gmod-lighter text-gmod-white rounded hover:bg-theme-primary"
                onClick={() => setShowUnbanModal(false)}
                disabled={pendingUnban[unbanTarget]}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActiveBansTab; 